import React, { useState } from 'react';

import styled from '@emotion/styled';

import { Alert, Button, Modal, Text } from '@shared/components/bootstrap';

import {
  Dialog as ConfirmationDialog,
  Response as ConfirmationResponse,
  useConfirmation,
} from '@admin/components/confirmation';
import { CopyText } from '@admin/components/helpers/copy_text';
import { Permission } from '@admin/components/helpers/permission';
import { WORKFORCE_IMPERSONATION } from '@admin/libraries/access_control_catalog.generated';
import { client } from '@admin/libraries/apollo';
import {
  Status,
  useUserImpersonateWorkforceLinkCreateMutation,
  useUserPasswordResetLinkGenerateMutation,
} from '@admin/schema';

const Column = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  max-width: 100%;
`;

export const GeneratePasswordResetLinkButton: React.FC<{
  userId: string;
  displayName: string;
}> = ({ userId, displayName }) => {
  const [generate, { loading }] = useUserPasswordResetLinkGenerateMutation({ client });
  const { confirmation, confirm } = useConfirmation();
  const [resultModalUrl, setResultModalUrl] = useState<string | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);

  const closeResultModal = () => setResultModalUrl(null);

  const onClick = async () => {
    const confirmed = await confirm({
      title: 'Generate Password Reset Link',
      description: (
        <>
          This will create a new password reset link for {displayName}. Any previous password reset links for this
          account will stop working. Continue?
        </>
      ),
    });
    if (confirmed !== ConfirmationResponse.Confirm) return;

    setMutationError(null);
    try {
      const response = await generate({ variables: { id: userId } });
      const payload = response.data?.result;
      if (payload?.status === Status.Unprocessable) {
        setResultModalUrl(null);
        setMutationError(typeof payload.error === 'string' ? payload.error : 'Something went wrong.');
        return;
      }
      const url = payload?.resetPasswordUrl;
      if (payload?.status === Status.Ok && url) {
        setResultModalUrl(url);
        return;
      }
      setResultModalUrl(null);
      setMutationError(typeof payload?.error === 'string' && payload.error ? payload.error : 'Something went wrong.');
    } catch (err: unknown) {
      setResultModalUrl(null);
      setMutationError(err instanceof Error ? err.message : 'Something went wrong.');
    }
  };

  return (
    <Column>
      {confirmation && <ConfirmationDialog confirmation={confirmation} />}
      {resultModalUrl && (
        <Modal onClose={closeResultModal} size={Modal.Size.Large}>
          <Modal.Content>
            <Modal.Header>
              <Modal.Title>Password reset link</Modal.Title>
              <Modal.Close close={closeResultModal} />
            </Modal.Header>
            <Modal.Body>
              <Text weight="bold">Copy and send this link securely</Text>
              <div className="mar-top">
                <Text tag="div" wrapping="wrap">
                  {resultModalUrl}
                </Text>
              </div>
              <div className="mar-top">
                <CopyText text={resultModalUrl} />
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button kind="primary" size="lg" onClick={closeResultModal}>
                Done
              </Button>
            </Modal.Footer>
          </Modal.Content>
        </Modal>
      )}
      <Button kind="warning" loading={loading} onClick={onClick}>
        Generate Password Reset Link
      </Button>
      {mutationError && <Text style="danger">{mutationError}</Text>}
    </Column>
  );
};

const AccessWorkforceConfirmationModal: React.FC<{ userID: string; displayName: string; onClose: () => void }> = ({
  userID,
  displayName,
  onClose,
}) => {
  const [execute, { loading }] = useUserImpersonateWorkforceLinkCreateMutation({
    client,
    variables: { userID },
  });
  const [error, setError] = useState<string | undefined>(undefined);

  const onConfirm = async () => {
    setError(undefined);
    const { data, errors } = await execute();

    setError(errors && errors[0].message);

    const url = data?.result.url;
    if (url) {
      open(url);
      onClose();
    }
  };

  return (
    <Modal onClose={onClose}>
      <Modal.Content>
        <Modal.Header>
          <Modal.Title>Access Workforce as {displayName}</Modal.Title>
          <Modal.Close close={onClose} />
        </Modal.Header>
        <Modal.Body>
          {error && (
            <Alert style="danger">
              <strong>Error: </strong>
              {error}
            </Alert>
          )}
          <Text>
            ATTENTION: You are opening Workforce as {displayName}. Any changes you make will be seen and attributed to
            them.
          </Text>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={onClose} disabled={loading} size="lg">
            Cancel
          </Button>
          <Button onClick={onConfirm} loading={loading} size="lg" kind="danger">
            Access Workforce
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
};

export const AccessWorkforceAccountButton: React.FC<{
  userID: string;
  displayName: string;
}> = ({ userID, displayName }) => {
  const [showModal, setShowModal] = useState<boolean>(false);

  return (
    <Permission
      resource={WORKFORCE_IMPERSONATION.resource}
      action={WORKFORCE_IMPERSONATION.actions.create}
      denied={null}
    >
      <Button kind="default" onClick={() => setShowModal(true)}>
        Access Workforce
      </Button>
      {showModal && (
        <AccessWorkforceConfirmationModal
          userID={userID}
          displayName={displayName}
          onClose={() => setShowModal(false)}
        />
      )}
    </Permission>
  );
};
